import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { ParticipantBrief } from '../types';
import { getAvatarGradient, getInitials } from '../utils/format';

// Task #1660 — popup mention `@` con keyboard nav (↑/↓/Enter/Esc).
// Render sopra al composer (absolute). Inserisce nel testo la sintassi
// canonica `@[Nome Cognome](uuid)` che il MessageItem renderizza con
// highlight + il backend salva in `messenger_messages.mentions` jsonb.

interface Props {
  open: boolean;
  query: string;
  participants: ParticipantBrief[];
  onPick: (user: ParticipantBrief) => void;
  onClose: () => void;
}

export function MentionPicker({ open, query, participants, onPick, onClose }: Props) {
  const [active, setActive] = useState(0);

  const filtered = participants
    .filter((p) => {
      if (!query) return true;
      const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
      return name.includes(query.toLowerCase()) || (p.email || '').toLowerCase().includes(query.toLowerCase());
    })
    .slice(0, 6);

  useEffect(() => { setActive(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(filtered.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter' || e.key === 'Tab') {
        if (filtered[active]) { e.preventDefault(); onPick(filtered[active]); }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, filtered, active, onClose, onPick]);

  if (!open || filtered.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 mb-1 w-72 bg-white dark:bg-gray-900 border rounded-lg shadow-lg z-30 max-h-56 overflow-auto">
      <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-b">
        Menziona — ↑↓ per navigare, Invio per inserire
      </div>
      {filtered.map((u, idx) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Utente';
        return (
          <button
            key={u.id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
            onMouseEnter={() => setActive(idx)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
              idx === active ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800',
            )}
          >
            <div className={cn('h-7 w-7 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-[10px] font-semibold shrink-0', getAvatarGradient(name))}>
              {getInitials(u.first_name, u.last_name)}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-xs truncate">{name}</div>
              {u.email && <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
